import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { ORDER_READY_SMS_EFFECTIVE, UPDATE_STORE_MESSAGING_SETTINGS } from "src/constants/mutations";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { failureToast, successToast } from "src/utils/toasts";

const MessagingSettingsPage = () => {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const storeCorrelationId = hasRestaurant?.correlationId;

  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!storeCorrelationId) return;
    setLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: ORDER_READY_SMS_EFFECTIVE,
        variables: { storeCorrelationId },
      });
      const err = data?.errors?.[0];
      if (err) throw new Error(err.message);
      const eff = data?.data?.orderReadySmsEffective;
      if (eff) {
        setPickupEnabled(!!eff.pickupEnabled);
        setDeliveryEnabled(!!eff.deliveryEnabled);
      }
    } catch (e: unknown) {
      failureToast(e instanceof Error ? e.message : "Could not load SMS settings");
    } finally {
      setLoading(false);
    }
  }, [axBe, storeCorrelationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!storeCorrelationId) return;
    setSaving(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: UPDATE_STORE_MESSAGING_SETTINGS,
        variables: {
          input: {
            storeCorrelationId,
            orderReadyPickupEnabled: pickupEnabled,
            orderReadyDeliveryEnabled: deliveryEnabled,
          },
        },
      });
      const err = data?.errors?.[0];
      if (err) throw new Error(err.message);
      successToast("SMS settings saved");
      void load();
    } catch (e: unknown) {
      failureToast(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!storeCorrelationId) {
    return <p className="text-sm text-gray-500">Select a restaurant first.</p>;
  }

  return (
    <div className="w-full max-w-[760px] space-y-6">
      <div className="bg-white rounded-[16px] px-6 py-5 shadow-[0_1px_3px_rgba(0,0,0,0.06),0_6px_20px_rgba(0,0,0,0.08)]">
        <h2 className="text-[18px] font-semibold text-[#111827] tracking-[-0.02em] mb-2">Text messaging (order ready)</h2>
        <p className="text-[14px] text-[#6b7280] mb-6">
          Control automatic SMS when an order is marked <strong>Ready</strong> for pickup or delivery. Customers must have a valid phone on the order.
          Dine-in orders never receive this SMS.
        </p>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <div className="space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] rounded border border-[#9B9B9B] cursor-pointer appearance-none"
                checked={pickupEnabled}
                onChange={(e) => setPickupEnabled(e.target.checked)}
              />
              <span className="text-[14px] text-[#374151]">Send order-ready SMS for pickup orders</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] rounded border border-[#9B9B9B] cursor-pointer appearance-none"
                checked={deliveryEnabled}
                onChange={(e) => setDeliveryEnabled(e.target.checked)}
              />
              <span className="text-[14px] text-[#374151]">Send order-ready SMS for delivery orders</span>
            </label>
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="mt-2 h-10 px-5 rounded-xl bg-[#FD7657] text-white font-medium hover:opacity-95 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagingSettingsPage;
